
import { useState } from 'react';
import type { AIProject } from '../../../../mocks/aiProjects';
import { useAuth } from '../../../../contexts/AuthContext';
import { AuthModal } from '../../../../components/feature/AuthModal';
import { useWatchlist } from '../../../../hooks/useWatchlist';
import { createAIProjectUrl } from '../../../../hooks/useNavigation';

interface ProjectVideoPlayerProps {
  project: AIProject;
}

const ProjectVideoPlayer = ({ project }: ProjectVideoPlayerProps) => {
  const { user } = useAuth();
  const { isInWatchlist, toggleWatchlist } = useWatchlist();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [watchlistToast, setWatchlistToast] = useState<{ show: boolean; added: boolean }>({ show: false, added: false });

  const inWatchlist = isInWatchlist(`ai-${project.id}`);

  const handleLike = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setIsLiked((prev) => !prev);
  };

  const handleSave = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    const added = toggleWatchlist({
      id: `ai-${project.id}`,
      type: 'ai-project',
      title: project.title,
      poster: project.thumbnail,
      rating: project.averageRating || 0,
      genre: project.category,
      year: project.datePublished.split('-')[0],
      duration: project.duration,
      url: createAIProjectUrl(project.slug),
    });
    setWatchlistToast({ show: true, added });
    setTimeout(() => setWatchlistToast({ show: false, added: false }), 2500);
  };

  return (
    <div>
      {/* Watchlist Toast */}
      {watchlistToast.show && (
        <div className="fixed top-20 right-6 z-50 animate-slide-in">
          <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border ${
            watchlistToast.added
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-gray-50 border-gray-200 text-gray-800'
          }`}>
            <div className={`w-8 h-8 flex items-center justify-center rounded-full ${
              watchlistToast.added ? 'bg-emerald-100' : 'bg-gray-100'
            }`}>
              <i className={`${watchlistToast.added ? 'ri-bookmark-fill text-emerald-600' : 'ri-bookmark-line text-gray-500'}`}></i>
            </div>
            <span className="text-sm font-medium">
              {watchlistToast.added ? 'Added to your watchlist!' : 'Removed from your watchlist'}
            </span>
          </div>
        </div>
      )}

      {/* Video Player */}
      <div className="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-2xl">
        {isPlaying ? (
          <iframe
            src={`${project.videoUrl}?autoplay=1&rel=0`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={project.title}
          />
        ) : (
          <>
            <img
              src={project.thumbnail}
              alt={project.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <button
                onClick={() => setIsPlaying(true)}
                className="w-20 h-20 flex items-center justify-center bg-white/20 backdrop-blur-md rounded-full border-2 border-white/40 hover:bg-white/30 hover:scale-110 transition-all cursor-pointer group"
              >
                <i className="ri-play-fill text-white text-4xl ml-1 group-hover:scale-110 transition-transform"></i>
              </button>
            </div>
            {/* Duration Badge */}
            <div className="absolute bottom-4 right-4 px-3 py-1.5 bg-black/70 backdrop-blur-sm rounded-lg text-white text-sm font-medium">
              {project.duration}
            </div>
            {/* Quality Badge */}
            <div className="absolute top-4 right-4">
              <span
                className={`px-2.5 py-1 rounded-lg text-white text-xs font-bold ${
                  project.quality === '4K'
                    ? 'bg-emerald-500'
                    : project.quality === 'HD'
                    ? 'bg-sky-500'
                    : 'bg-slate-500'
                }`}
              >
                {project.quality}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Video Actions Bar */}
      <div className="flex items-center justify-between mt-4 px-1">
        <div className="flex items-center gap-6 text-sm text-slate-400">
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line text-base"></i>
            {project.views} views
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line text-base"></i>
            {new Date(project.datePublished).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleLike}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              isLiked
                ? 'bg-rose-500/20 text-rose-400 border border-rose-500/40'
                : 'bg-slate-700/50 text-slate-300 border border-slate-600 hover:bg-slate-600/50'
            }`}
          >
            <i className={`${isLiked ? 'ri-heart-fill' : 'ri-heart-line'} text-base`}></i>
            {project.likes}
          </button>
          <button
            onClick={handleSave}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
              inWatchlist
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                : 'bg-slate-700/50 text-slate-300 border border-slate-600 hover:bg-slate-600/50'
            }`}
          >
            <i className={`${inWatchlist ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-base`}></i>
            {inWatchlist ? 'Saved' : 'Save'}
          </button>
          <div className="relative">
            <button
              onClick={() => setShowShareMenu((prev) => !prev)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-slate-700/50 text-slate-300 border border-slate-600 hover:bg-slate-600/50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-forward-line text-base"></i>
              Share
            </button>
            {showShareMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowShareMenu(false)}
                ></div>
                <div className="absolute right-0 top-full mt-2 w-48 bg-slate-800 border border-slate-700 rounded-xl shadow-xl z-20 py-2">
                  <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700/50 transition-colors cursor-pointer">
                    <i className="ri-twitter-x-line"></i>
                    Share on X
                  </button>
                  <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700/50 transition-colors cursor-pointer">
                    <i className="ri-facebook-circle-line"></i>
                    Share on Facebook
                  </button>
                  <button className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-700/50 transition-colors cursor-pointer">
                    <i className="ri-links-line"></i>
                    Copy Link
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />
    </div>
  );
};

export default ProjectVideoPlayer;
